import React, { useEffect, useState } from "react";
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import PermissionGate from "../../Components/PermissionGate/PermissionGate";
import { MODULES } from "../../utils/permissions";
const ContentManagementSystem = () => {
  const [activeTab, setActiveTab] = useState("terms");
  const [isEditing, setIsEditing] = useState(false);
  const [loadingCms, setLoadingCms] = useState(true);
  const [savingCms, setSavingCms] = useState(false);

  const [content, setContent] = useState({
    terms: "",
    privacy: "",
  });

  const handleChange = (value) => {
    setContent({
      ...content,
      [activeTab]: value,
    });
  };
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const extractCmsHtml = (payload) => {
    if (payload == null) return "";
    if (typeof payload === "string") return payload;
    if (typeof payload === "object") {
      if (typeof payload.description === "string") return payload.description;
      if (typeof payload.content === "string") return payload.content;
      if (typeof payload.cms === "string") return payload.cms;
      if (payload.data) return extractCmsHtml(payload.data);
      if (Array.isArray(payload) && payload[0]) return extractCmsHtml(payload[0]);
    }
    return "";
  };

  const decodeHtmlEntities = (value) => {
    if (typeof value !== "string") return "";
    if (!value.includes("&")) return value;
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    return doc.documentElement.textContent || "";
  };

  useEffect(() => {
    const fetchCms = async () => {
      setLoadingCms(true);
      try {
        const [termsRes, privacyRes] = await Promise.all([
          handleApiCall("get", `${endpoint.getCms}?type=term_and_condition`, {}),
          handleApiCall("get", `${endpoint.getCms}?type=privacy_policy`, {}),
        ]);

        const next = { terms: "", privacy: "" };

        if (termsRes?.data?.code === 200) {
          next.terms = decodeHtmlEntities(extractCmsHtml(termsRes?.data?.data));
        } else {
          const errMsg =
            termsRes?.response?.data?.errorMessage ||
            termsRes?.response?.data?.message ||
            termsRes?.data?.message;
          if (errMsg) notifyError(errMsg);
        }

        if (privacyRes?.data?.code === 200) {
          next.privacy = decodeHtmlEntities(extractCmsHtml(privacyRes?.data?.data));
        } else {
          const errMsg =
            privacyRes?.response?.data?.errorMessage ||
            privacyRes?.response?.data?.message ||
            privacyRes?.data?.message;
          if (errMsg) notifyError(errMsg);
        }

        setContent(next);
      } catch {
        notifyError("Failed to fetch CMS data");
      } finally {
        setLoadingCms(false);
      }
    };

    fetchCms();
  }, []);

  const tabMeta =
    activeTab === "terms"
      ? { type: "term_and_condition", title: "Term And Condition" }
      : { type: "privacy_policy", title: "Privacy Policy" };

  const handleEditSave = async () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    const description = (content[activeTab] || "").trim();
    if (!description) {
      notifyError("Content is required");
      return;
    }

    setSavingCms(true);
    try {
      const response = await handleApiCall("post", endpoint.updateCms, {
        type: tabMeta.type,
        title: tabMeta.title,
        description,
      });
      if (response?.data?.code === 200) {
        notifySuccess(response?.data?.message || "CMS updated successfully");
        setIsEditing(false);
        return;
      }
      const errMsg =
        response?.response?.data?.errorMessage ||
        response?.response?.data?.message ||
        response?.data?.message ||
        "Failed to update CMS";
      notifyError(errMsg);
    } catch {
      notifyError("Something went wrong. Please try again.");
    } finally {
      setSavingCms(false);
    }
  };

  return (
    <>
    <Toastify/>
    <div className="font-UbuntuRegular !mt-4 md:!mt-4">
      <div className="flex justify-between items-center md:!mb-4 !mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("terms")}
            disabled={savingCms}
            className={`btn !rounded-full border md:!py-3 md:!px-6 !py-2 !px-4 max-[767px]:!text-xs ${
              activeTab === "terms"
                ? "!bg-danger border-danger text-white"
                : "border-black text-black hover:bg-danger hover:!border-danger"
            }`}
          >
            Terms & Conditions
          </button>

          <button
            onClick={() => setActiveTab("privacy")}
            disabled={savingCms}
            className={`btn !rounded-full border md:!py-3 md:!px-6 !py-2 !px-4 max-[767px]:!text-xs ${
              activeTab === "privacy"
                ? "!bg-danger border-danger text-white"
                : "border-black text-black hover:bg-danger hover:!border-danger"
            }`}
          >
            Privacy Policy
          </button>
        </div>

        <PermissionGate module={MODULES.CMS} action="edit">
          <button
            onClick={handleEditSave}
            className="btn btn-primary"
            disabled={loadingCms || savingCms}
          >
            {savingCms ? "Saving..." : isEditing ? "Save" : "Edit"}
          </button>
        </PermissionGate>
      </div>

      <div className="bg-white border rounded md:!p-4 !p-3 shadow-sm">
        {loadingCms ? (
          <div className="py-10 text-center text-gray-500">Loading...</div>
        ) : isEditing ? (
          <ReactQuill
            theme="snow"
            value={content[activeTab]}
            onChange={handleChange}
            className="[&_.ql-editor]:min-h-[260px] [&_.ql-editor]:max-h-[260px] [&_.ql-editor]:overflow-y-auto"
            modules={modules}
          />
        ) : (
          <div
            className="prose max-w-none [&_strong]:font-UbuntuBold [&_b]:font-UbuntuBold [&_h1]:font-UbuntuBold [&_h2]:font-UbuntuBold [&_h3]:font-UbuntuBold"
            dangerouslySetInnerHTML={{ __html: content[activeTab] }}
          />
        )}
      </div>
    </div>
    </>
  );
};

export default ContentManagementSystem;
